import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import InsuranceCard from '@components/Patient/InsuranceCard';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import { useTrackEvent } from '@jotpsych/statsig';
import AddIcon from '@mui/icons-material/Add';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { EventNames } from '@services/statsig/constants';
import { useSnackbar } from 'notistack';

import SentryService from '../../../services/SentryService';
import type { InsuranceInfo } from '../../../types/types';
import {
  useCreatePatientCoverageMutation,
  useDeletePatientCoverageMutation,
  useUpdatePatientCoverageMutation,
} from '../store/api';
import {
  isSelfPayCoverage,
  mapInsuranceToCoverageRequest,
  useOrganizedPatientCoverages,
} from '../store/hooks';
import type { ClientErrorResponse, PatientCoverage } from '../types';

import { CoverageGapNotice } from './CoverageGapNotice';
import { CoverageStatusChip } from './CoverageStatusChip';
import { InsuranceHistorySection } from './InsuranceHistorySection';
import { SelfPayCoverageCard, SelfPayCoverageToggle } from './SelfPayCoverageDesignation';

type CoverageSlot =
  | 'primary_insurance'
  | 'incoming_primary_insurance'
  | 'secondary_insurance'
  | 'tertiary_insurance';

const SLOT_TO_LOCAL_KEY: Record<
  CoverageSlot,
  'primary' | 'incomingPrimary' | 'secondary' | 'tertiary'
> = {
  primary_insurance: 'primary',
  incoming_primary_insurance: 'incomingPrimary',
  secondary_insurance: 'secondary',
  tertiary_insurance: 'tertiary',
};

// The incoming plan shares coverage_order 1 with the current primary: it is
// a successor on the time axis (GDN-2411), not a COB secondary. The backend
// resolves which one bills per date of service.
const SLOT_TO_ORDER: Record<CoverageSlot, number> = {
  primary_insurance: 1,
  incoming_primary_insurance: 1,
  secondary_insurance: 2,
  tertiary_insurance: 3,
};

type CoverageAutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface PatientInsuranceAutosaveState {
  isDirty: boolean;
  lastSavedAt?: Date | null;
  status: CoverageAutosaveStatus;
}

interface PatientInsuranceEditorProps {
  errors?: Record<string, string>;
  highlightedFields?: string[];
  onAutosaveStateChange?: (state: PatientInsuranceAutosaveState) => void;
  onCoverageSave?: () => void;
  patientId: string;
  showInlineAutosaveStatus?: boolean;
  sx?: SxProps<Theme>;
}

const MISSING_FIELD_LABELS: Record<string, string> = {
  member_id: 'Member/Policy ID',
  payer_name: 'Payer Name',
  relationship_to_subscriber: 'Relationship',
  subscriber_first_name: 'Subscriber First Name',
  subscriber_last_name: 'Subscriber Last Name',
  subscriber_dob: 'Subscriber Date of Birth',
};

export const PatientInsuranceEditor: React.FC<PatientInsuranceEditorProps> = ({
  errors = {},
  highlightedFields = [],
  onAutosaveStateChange,
  onCoverageSave,
  patientId,
  showInlineAutosaveStatus = false,
  sx,
}) => {
  const {
    organized: organizedCoverages,
    history: coverageHistory,
    coverageInForceToday,
    nextCoverageGap,
    refetch: refetchCoverages,
  } = useOrganizedPatientCoverages(patientId);

  const [createCoverage] = useCreatePatientCoverageMutation();
  const [updateCoverage] = useUpdatePatientCoverageMutation();
  const [deleteCoverage] = useDeletePatientCoverageMutation();
  const subjectLanguageVariants = useSubjectLanguageVariants();
  const { enqueueSnackbar } = useSnackbar();
  const { trackEvent } = useTrackEvent();

  const [showSecondaryInsurance, setShowSecondaryInsurance] = useState(false);
  const [showTertiaryInsurance, setShowTertiaryInsurance] = useState(false);
  const [showIncomingPlan, setShowIncomingPlan] = useState(false);
  const [hasPendingCoverageChanges, setHasPendingCoverageChanges] = useState(false);
  const [localCoverages, setLocalCoverages] = useState<{
    primary?: InsuranceInfo | PatientCoverage | null;
    incomingPrimary?: InsuranceInfo | PatientCoverage | null;
    secondary?: InsuranceInfo | PatientCoverage | null;
    tertiary?: InsuranceInfo | PatientCoverage | null;
  }>({});

  const pendingCoverageRef = useRef<{
    slot: CoverageSlot;
    insurance: InsuranceInfo | PatientCoverage | null;
  } | null>(null);

  useEffect(() => {
    setLocalCoverages({
      primary: organizedCoverages.primary,
      incomingPrimary: organizedCoverages.incomingPrimary,
      secondary: organizedCoverages.secondary,
      tertiary: organizedCoverages.tertiary,
    });
    setShowSecondaryInsurance(!!organizedCoverages.secondary);
    setShowTertiaryInsurance(!!organizedCoverages.tertiary);
    setShowIncomingPlan(!!organizedCoverages.incomingPrimary);
    setHasPendingCoverageChanges(false);
  }, [organizedCoverages]);

  const existingCoverageForSlot = useCallback(
    (slot: CoverageSlot): PatientCoverage | undefined => {
      switch (slot) {
        case 'primary_insurance':
          return organizedCoverages.primary;
        case 'incoming_primary_insurance':
          return organizedCoverages.incomingPrimary;
        case 'secondary_insurance':
          return organizedCoverages.secondary;
        default:
          return organizedCoverages.tertiary;
      }
    },
    [organizedCoverages],
  );

  const performCoverageSave = useCallback(
    async (data: { slot: CoverageSlot; insurance: InsuranceInfo | PatientCoverage | null }) => {
      const { slot, insurance } = data;
      const coverageOrder = SLOT_TO_ORDER[slot];

      try {
        const existingCoverage = existingCoverageForSlot(slot);

        if (!insurance) {
          if (existingCoverage?.patient_insurance_coverage_id) {
            await deleteCoverage({
              patientId,
              coverageId: existingCoverage.patient_insurance_coverage_id,
            }).unwrap();
          }
        } else {
          const coverageData = mapInsuranceToCoverageRequest(coverageOrder, insurance);

          if (existingCoverage?.patient_insurance_coverage_id) {
            await updateCoverage({
              patientId,
              coverageId: existingCoverage.patient_insurance_coverage_id,
              updateCoverageRequest: coverageData,
            }).unwrap();
          } else {
            await createCoverage({
              patientId,
              createCoverageRequest: coverageData,
            }).unwrap();
          }
        }

        await refetchCoverages();
        setHasPendingCoverageChanges(false);
        onCoverageSave?.();
      } catch (error: unknown) {
        const errorData = (error as { data?: ClientErrorResponse })?.data;
        // Auto-supersede's one human-input case (GDN-2411): two active
        // plans in a slot with no dates to order them. Tell the user what
        // resolves it instead of failing silently into Sentry.
        if (errorData?.error_code === 'coverage_order_conflict') {
          enqueueSnackbar(
            errorData.message ||
              'Another active plan occupies this position. Add an effective or termination date to indicate which plan is current.',
            { variant: 'warning' },
          );
        }
        SentryService.logEvent('Error updating coverage', {
          level: 'error',
          tags: {
            patient_id: patientId,
            coverage_order: String(coverageOrder),
            error_code: errorData?.error_code || 'unknown',
          },
          extra: {
            error,
            error_message: errorData?.message,
          },
        });
        throw error;
      }
    },
    [
      createCoverage,
      deleteCoverage,
      enqueueSnackbar,
      existingCoverageForSlot,
      onCoverageSave,
      patientId,
      refetchCoverages,
      updateCoverage,
    ],
  );

  const {
    triggerAutoSave: triggerCoverageAutoSave,
    saveImmediately: saveCoverageImmediately,
    status: coverageAutoSaveStatus,
    lastSavedAt: coverageAutoSaveLastSavedAt,
  } = useAutoSave<{ slot: CoverageSlot; insurance: InsuranceInfo | PatientCoverage | null }>({
    onSave: performCoverageSave,
    debounceMs: 2000,
    flushOnUnmount: true,
  });

  useEffect(() => {
    onAutosaveStateChange?.({
      isDirty: hasPendingCoverageChanges,
      lastSavedAt: coverageAutoSaveLastSavedAt,
      status: coverageAutoSaveStatus,
    });
  }, [
    coverageAutoSaveLastSavedAt,
    coverageAutoSaveStatus,
    hasPendingCoverageChanges,
    onAutosaveStateChange,
  ]);

  const handleInsuranceChange = useCallback(
    (insuranceType: CoverageSlot, insurance: InsuranceInfo | PatientCoverage | null) => {
      setLocalCoverages(prev => ({
        ...prev,
        [SLOT_TO_LOCAL_KEY[insuranceType]]: insurance,
      }));

      setHasPendingCoverageChanges(true);
      pendingCoverageRef.current = { slot: insuranceType, insurance };

      // Saves are intentionally NOT gated on subscriber completeness
      // (GDN-2317): most existing dependent coverages are incomplete, and
      // blocking here would silently discard unrelated edits (payer,
      // member ID, ...). Partial subscriber data persisting progressively
      // is fine — enforcement lives at claim submission (backend scrub +
      // CMS-1500 banner); the editor only guides completion via the
      // inline required errors in InsuranceCard.
      triggerCoverageAutoSave({ slot: insuranceType, insurance });
    },
    [triggerCoverageAutoSave],
  );

  const handleCoverageBlur = useCallback(() => {
    if (pendingCoverageRef.current && hasPendingCoverageChanges) {
      saveCoverageImmediately(pendingCoverageRef.current);
      pendingCoverageRef.current = null;
    }
  }, [hasPendingCoverageChanges, saveCoverageImmediately]);

  const handleSelfPaySaved = useCallback(() => {
    setHasPendingCoverageChanges(false);
    pendingCoverageRef.current = null;
    onCoverageSave?.();
  }, [onCoverageSave]);

  const primaryIsSelfPay = isSelfPayCoverage(localCoverages.primary);

  return (
    <Stack spacing={3} sx={sx}>
      {showInlineAutosaveStatus && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AutosaveStatus
            status={coverageAutoSaveStatus}
            isDirty={hasPendingCoverageChanges}
            lastSavedAt={coverageAutoSaveLastSavedAt}
            verbose
          />
        </Box>
      )}

      <Box>
        <SelfPayCoverageToggle
          patientId={patientId}
          primaryCoverage={localCoverages.primary}
          refetchCoverages={refetchCoverages}
          onAfterSave={handleSelfPaySaved}
          onCoverageSave={onCoverageSave}
          containerSx={{ mb: 2 }}
        />
      </Box>

      <CoverageGapNotice
        coverageInForceToday={coverageInForceToday}
        nextCoverageGap={nextCoverageGap}
      />

      {primaryIsSelfPay ? (
        <SelfPayCoverageCard subjectLabel={subjectLanguageVariants.singular} />
      ) : (
        <>
          <CoveragePendingWarning coverage={localCoverages.primary} />
          <InsuranceCard
            insurance={localCoverages.primary ?? null}
            onChange={insurance => handleInsuranceChange('primary_insurance', insurance)}
            onBlur={handleCoverageBlur}
            title="Primary Insurance"
            statusChip={
              localCoverages.primary && 'status' in localCoverages.primary ? (
                <CoverageStatusChip coverage={localCoverages.primary} />
              ) : undefined
            }
            patientId={patientId}
            errors={errors}
            highlightedFields={highlightedFields}
          />

          {/* Succession, not COB (GDN-2411): the incoming plan shares the
              primary position and takes over on its effective date — it is
              NOT a secondary. Billing resolves per date of service. */}
          {showIncomingPlan && (
            <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'info.light' }}>
              <CoveragePendingWarning coverage={localCoverages.incomingPrimary} />
              <InsuranceCard
                insurance={localCoverages.incomingPrimary ?? null}
                onChange={insurance =>
                  handleInsuranceChange('incoming_primary_insurance', insurance)
                }
                onBlur={handleCoverageBlur}
                title="Incoming Plan"
                statusChip={
                  localCoverages.incomingPrimary &&
                  'status' in localCoverages.incomingPrimary ? (
                    <CoverageStatusChip coverage={localCoverages.incomingPrimary} />
                  ) : undefined
                }
                patientId={patientId}
                showDelete
                onDelete={() => {
                  setShowIncomingPlan(false);
                  handleInsuranceChange('incoming_primary_insurance', null);
                }}
                errors={errors}
                highlightedFields={highlightedFields}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
              >
                Set the start date this plan takes effect. The current plan
                keeps billing visits before that date; this one takes over
                automatically after it.
              </Typography>
            </Box>
          )}
        </>
      )}

      {!primaryIsSelfPay && showSecondaryInsurance && (
        <Box>
          <CoveragePendingWarning coverage={localCoverages.secondary} />
          <InsuranceCard
            insurance={localCoverages.secondary ?? null}
            onChange={insurance => handleInsuranceChange('secondary_insurance', insurance)}
            onBlur={handleCoverageBlur}
            title="Secondary Insurance"
            statusChip={
              localCoverages.secondary && 'status' in localCoverages.secondary ? (
                <CoverageStatusChip coverage={localCoverages.secondary} />
              ) : undefined
            }
            patientId={patientId}
            showDelete
            onDelete={() => {
              setShowSecondaryInsurance(false);
              handleInsuranceChange('secondary_insurance', null);
            }}
            errors={errors}
            highlightedFields={highlightedFields}
          />
        </Box>
      )}

      {!primaryIsSelfPay && showTertiaryInsurance && (
        <Box>
          <CoveragePendingWarning coverage={localCoverages.tertiary} />
          <InsuranceCard
            insurance={localCoverages.tertiary ?? null}
            onChange={insurance => handleInsuranceChange('tertiary_insurance', insurance)}
            onBlur={handleCoverageBlur}
            title="Tertiary Insurance"
            statusChip={
              localCoverages.tertiary && 'status' in localCoverages.tertiary ? (
                <CoverageStatusChip coverage={localCoverages.tertiary} />
              ) : undefined
            }
            patientId={patientId}
            showDelete
            onDelete={() => {
              setShowTertiaryInsurance(false);
              handleInsuranceChange('tertiary_insurance', null);
            }}
            errors={errors}
            highlightedFields={highlightedFields}
          />
        </Box>
      )}

      {!primaryIsSelfPay && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {/* Two visibly distinct actions (GDN-2411): "incoming plan" =
              the patient is SWITCHING insurance (succession — new plan
              takes over on its start date); "secondary" = the patient has
              two plans AT THE SAME TIME (COB — pays after the primary). */}
          {!showIncomingPlan && !!localCoverages.primary && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EventRepeatIcon />}
              onClick={() => {
                trackEvent(EventNames.INSURANCE_ADD_INCOMING_PLAN_CLICKED, {
                  metadata: { patient_id: patientId },
                });
                setShowIncomingPlan(true);
              }}
            >
              Switching insurance? Add incoming plan
            </Button>
          )}
          {!showSecondaryInsurance && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowSecondaryInsurance(true)}
            >
              Add Secondary Insurance
            </Button>
          )}
          {showSecondaryInsurance && !showTertiaryInsurance && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowTertiaryInsurance(true)}
            >
              Add Tertiary Insurance
            </Button>
          )}
        </Box>
      )}

      <InsuranceHistorySection
        history={coverageHistory}
        onOpen={() =>
          trackEvent(EventNames.INSURANCE_HISTORY_OPENED, {
            metadata: {
              patient_id: patientId,
              history_count: coverageHistory.length,
            },
          })
        }
      />
    </Stack>
  );
};

function CoveragePendingWarning({
  coverage,
}: {
  coverage?: InsuranceInfo | PatientCoverage | null;
}) {
  const missingFields =
    coverage &&
    'status' in coverage &&
    coverage.status === 'pending' &&
    'missing_fields' in coverage
      ? coverage.missing_fields
      : undefined;

  if (!missingFields?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: 'warning.light',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <WarningAmberIcon color="warning" fontSize="small" />
      <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
        Coverage is pending. Missing:{' '}
        {missingFields.map(field => MISSING_FIELD_LABELS[field] || field).join(', ')}
      </Typography>
    </Box>
  );
}
